import * as React from "react";
import { Grid, Typography, Paper, Divider, Box, Link } from "@mui/material";
import { styled } from "@mui/system";
import { useHistory } from "react-router-dom";
import { PrivilegesContext } from "../PrivilegesContext";
import { useCustomerPermission } from "../hooks/useCustomerPermission";
import { capitalize, formatZip } from "../utils/StringHelper";
import FollowUpForm from "../modals/FollowUpForm";
import { formatToUsDateFromUTCNew } from "../utils/DateHelper";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";
import { useFlash } from "../FlashContext";

// Use the same image paths as in PatientShow.tsx
const addFollowupIcon = require("../../images/followupAddIcon.svg");
const qrIcon = require("../../images/qrIcon.svg");
const futureIcon1 = require("../../images/futureIcon1.svg");
const futureIcon2 = require("../../images/futureIcon2.svg");
const futureIcon3 = require("../../images/futureIcon3.svg");
const infoIcon = require("../../images/infoIcon.svg");

interface Props {
  selectedPatient: any;
  patient_reports_icon: string;
  setQrModalOpen: (open: boolean) => void;
}

const ActionButton = styled(Link)({
  width: "100%",
  height: "100%",
  padding: "12px",
  borderRadius: "12px",
  textTransform: "none",
  borderColor: "#E0E0E0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#FFFFFF",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: "#F5F5F5",
  },
});

const PatientInfoCard: React.FC<Props> = (props: any) => {
  const userPrivileges = React.useContext<any>(PrivilegesContext);
  const { showToast } = useFlash();
  const { permitted: tabletPermitted } = useCustomerPermission(
    "Allow questionnaires to display on local device"
  );
  const history = useHistory();
  const [actionModalOpen, setActionModalOpen] = React.useState<boolean>(false);

  const getAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleQuestionnaireModelClick = () => {
    props.setQrModalOpen(true);
  };

  const generateTags = (patient: any) => {
    const tags = [];

    if (patient.cgm_enabled) {
      tags.push({ label: "CGM", color: "#FF890A" });
    }

    if (patient.program_titles && patient.program_titles.length > 0) {
      tags.push({
        label: "Programs",
        color: "#EBB708",
        count: patient.program_titles.length.toString(),
      });
    }

    if (patient.ltc_facility_names && patient.ltc_facility_names.length > 0) {
      tags.push({ label: "Living Facility", color: "#07BC2A" });
    }

    if (patient.patient_insurance) {
      tags.push({
        label: capitalize(patient.patient_insurance.insurance_type),
        color: "#3F51B5",
        // count: patient.insurance_types_list.length.toString(),
      });
    }

    if (
      patient.patient_forecast_immunizations &&
      patient.patient_forecast_immunizations.length > 0
    ) {
      tags.push({
        label: "Immunizations",
        color: "#F00",
        count: patient.patient_forecast_immunizations.length.toString(),
      });
    }

    if (
      patient.diagnosis_code_values &&
      patient.diagnosis_code_values.length > 0
    ) {
      tags.push({
        label: "Diagnosis Codes",
        color: "#AE5C04",
        count: patient.diagnosis_code_values.length.toString(),
      });
    }

    if (
      patient.invitation_accepted === false &&
      patient.user_creation_type == "invited"
    ) {
      tags.push({ label: "Invitation Pending", color: "#FFA500" });
    }

    if (patient.list_status && patient.list_status !== "Not on List") {
      tags.push({ label: patient.list_status, color: "#800080" });
    }

    return tags;
  };

  const patientTags = generateTags(props.selectedPatient);

  console.log("selectedPatient", props.selectedPatient);

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      className="patient-info"
    >
      {/* Patient Info Card (left side) */}
      <Grid item style={{ flexBasis: "85%" }}>
        <Paper
          elevation={3}
          sx={{
            padding: "24px 24px 32px 30px",
            boxShadow: "none",
            borderRadius: 0,
            backgroundColor: "#FFFFFF",
          }}
        >
          <Grid container spacing={2}>
            {/* Header Section */}
            <Grid
              item
              xs={12}
              container
              alignItems="center"
              style={{ padding: "15px 0px 0px 15px" }}
            >
              <Grid
                item
                style={{
                  background: "#EFE9E7",
                  borderRadius: "8px",
                  width: "42px",
                  height: "42px",
                }}
              >
                <img
                  src={infoIcon}
                  alt="Info"
                  style={{ margin: "12px 0px 0 12px" }}
                />
              </Grid>
              <Grid item style={{ marginLeft: "20px" }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", fontSize: "16px", color: "#000" }}
                >
                  {props.selectedPatient.last_name},{" "}
                  {props.selectedPatient.first_name}
                  {props.selectedPatient.middle_name
                    ? ` (${props.selectedPatient.middle_name})`
                    : ""}{" "}
                  <span style={{ color: "#757575", marginLeft: "20px" }}>
                    MRN: {props.selectedPatient.mrn_number}
                  </span>
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#707070", fontSize: "14px" }}
                >
                  {getAge(props.selectedPatient.date_of_birth)} years old
                  {props.selectedPatient.gender && " •"}{" "}
                  {props.selectedPatient.gender}
                  {props.selectedPatient.ethnicity && " •"}{" "}
                  {props.selectedPatient.ethnicity}
                  {props.selectedPatient.race && " •"}{" "}
                  {props.selectedPatient.race}
                </Typography>
              </Grid>
            </Grid>

            {/* Divider */}
            <Grid item xs={12} style={{ padding: "22px 0px 3px 0px" }}>
              <Divider />
            </Grid>

            {/* Patient Details */}
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  color: "var(--light-grey, #A29D9B)",
                  fontSize: "10px",
                  marginBottom: "6px",
                }}
              >
                Name (Last, First, Middle)
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#4A4442",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {props.selectedPatient.last_name}
                <br />
                {props.selectedPatient.first_name}
                <br />
                {props.selectedPatient.middle_name || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  color: "var(--light-grey, #A29D9B)",
                  fontSize: "10px",
                  marginBottom: "6px",
                }}
              >
                Address
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#4A4442",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {props.selectedPatient.address ? (
                  <>
                    {props.selectedPatient.address}
                    <br />
                    {props.selectedPatient.address_line_2 && (
                      <>
                        {props.selectedPatient.address_line_2}
                        <br />
                      </>
                    )}
                    {`${props.selectedPatient.city}, ${
                      props.selectedPatient.state
                    } ${formatZip(props.selectedPatient.zip)}`}
                  </>
                ) : (
                  "Address Missing"
                )}
                <br />
                {props.selectedPatient.county}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  color: "#A29D9B",
                  fontSize: "10px",
                  marginBottom: "6px",
                }}
              >
                Demographics
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#4A4442",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                DOB:{" "}
                {formatToUsDateFromUTCNew(
                  props.selectedPatient.date_of_birth
                ) || "N/A"}
                {props.selectedPatient.ethnicity ||
                props.selectedPatient.race ? (
                  <>
                    <br />
                    {[
                      props.selectedPatient.ethnicity,
                      props.selectedPatient.race,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </>
                ) : null}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  color: "var(--light-grey, #A29D9B)",
                  fontSize: "10px",
                  marginBottom: "5px",
                }}
              >
                Contact
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "#4A4442",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {props.selectedPatient.email}
                <br />
                {props.selectedPatient.mobile_phone_number}
                {props.selectedPatient?.contact_channel && (
                  <>
                    <br />
                    {`Preferred Contact Channel: ${snakeCaseToTitleCase(
                      props.selectedPatient?.contact_channel
                    )}`}
                  </>
                )}
                {props.selectedPatient?.contact_method && (
                  <>
                    <br />
                    {`Preferred Contact Method: ${snakeCaseToTitleCase(
                      props.selectedPatient?.contact_method
                    )}`}
                  </>
                )}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Action Buttons (right side) */}
      <Grid item className="action-button-container">
        <ActionButton
          className="action-button"
          onClick={tabletPermitted ? handleQuestionnaireModelClick : undefined}
          sx={{ display: "flex" }}
        >
          <img
            src={qrIcon}
            alt="Guided Questionnaire"
            style={{
              width: 40,
              marginBottom: 8,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              fontWeight: "700",
              color: "#A29D9B",
              textAlign: "center",
              lineHeight: 1,
            }}
          >
            Guided Questionnaire
          </Typography>
        </ActionButton>

        <ActionButton
          href={`/patient_reports/${props.selectedPatient?.id}/action_center`}
          className="action-button"
        >
          <img
            src={props.patient_reports_icon}
            alt="Profile"
            style={{ width: 40, marginBottom: 8 }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              fontWeight: "700",
              color: "#A29D9B",
              textAlign: "center",
            }}
          >
            Profile
          </Typography>
        </ActionButton>

        <ActionButton
          onClick={() => setActionModalOpen(true)}
          className="action-button"
        >
          <img
            src={addFollowupIcon}
            alt="Create Quick Action"
            style={{ width: 40, marginBottom: 8 }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              fontWeight: "700",
              color: "#A29D9B",
              textAlign: "center",
            }}
          >
            Create Quick Action
          </Typography>
        </ActionButton>

        {/* <ActionButton sx={{ width: "80px", height: "80px" }}>
              <img
                src={futureIcon1}
                alt="Future Button"
                style={{ width: 40, marginBottom: 8 }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "#A29D9B",
                  textAlign: "center",
                }}
              >
                Future Button
              </Typography>
            </ActionButton> */}
      </Grid>

      {/* Patient Tags */}
      <Grid item xs={12}>
        <Paper
          elevation={3}
          sx={{
            padding: "15px 0 15px 30px",
            boxShadow: "none",
            borderRadius: 0,
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="subtitle2"
              sx={{
                marginRight: 2,
                color: "#757575",
                fontSize: "13px",
                fontWeight: "700",
              }}
            >
              Patient Tags:
            </Typography>
            <Box display="flex" gap={3}>
              {patientTags.map((tag, index) => (
                <Box
                  key={index}
                  sx={{
                    bgcolor: "#F5F5F5",
                    p: "4px 8px",
                    fontSize: "10px",
                    color: "#000",
                    borderLeft: `4px solid ${tag.color}`,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {tag.label}
                  {tag.count && (
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        borderRadius: "80px",
                        display: "flex",
                        alignItems: "center",
                        width: "23px",
                        justifyContent: "center",
                        textAlign: "center",
                        marginLeft: "5px",
                        padding: "1px 2px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          marginLeft: 1,
                          color: "#000",
                          fontWeight: "bold",
                          fontSize: "10px",
                          margin: "0px",
                        }}
                      >
                        {tag.count}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Grid>
      <FollowUpForm
        patient_id={props.selectedPatient.id}
        modalOpen={actionModalOpen}
        setModalOpen={setActionModalOpen}
        showToast={showToast}
        />
    </Grid>
  );
};

export default PatientInfoCard;
